/**
 * NGAR2Geocoder - Geocodificador NGA R2 (split por municipio)
 *
 * Geocodifica topónimos rurales usando datos NGA desde Worker R2.
 * Cada municipio se carga lazy on-demand mediante NGAR2Service.
 *
 * @module services/geocoding/specialized
 * @version 2.0.0
 * @date 2026-02-06
 */

import { getNGAR2Service, resetNGAR2Service } from './NGAR2Service';
import type { NGAR2Service } from './NGAR2Service';
import type {
  NgaR2Feature,
  NgaR2GeocodingOptions,
  NgaR2GeocodingResult,
} from '../../../types/ngaR2';

/**
 * Tipos NGA cuyo punto representa el centroide de una zona extensa.
 * Valores empíricos verificados contra R2 (2026-03-29 · ampliado 2026-04-06).
 * Error real: ±80–500 m. Penalización: −15 pts, precision_m ≥ 80.
 */
const NGA_ZONE_TYPES = new Set([
  'Lugar / Paraje',
  'Llanura',
  'Depresión',
  'Relieve Litoral / Marítimo',
  'Masa de Agua',
  'Núcleo de Población',  // centroide del núcleo: error real ±100–500 m según tamaño
]);

/**
 * Tipos NGA cuyo punto representa el punto medio de un elemento lineal.
 * Valores empíricos verificados contra R2 (2026-03-29).
 * Error real: ±30–150 m. Penalización: −5 pts, precision_m ≥ 30.
 */
const NGA_LINEAR_TYPES = new Set([
  'Curso Fluvial',
  'Vía de Comunicación',
]);

/** Score base según calidad del match */
const SCORE_CONFIG = {
  exact: 95,
  fuzzyHigh: 85,
  fuzzyMedium: 75,
  fuzzyLow: 65,
  withMunicipioBonus: 5,
  withTypeBonus: 3,
};

/** Precisión estimada según tipo de match */
const PRECISION_CONFIG = {
  exact: 15,
  fuzzyHigh: 25,
  fuzzyMedium: 35,
  fuzzyLow: 50,
};

/**
 * Geocodificador NGA R2 - lazy por municipio
 */
class NGAR2Geocoder {
  private static instance: NGAR2Geocoder | null = null;
  private service: NGAR2Service;

  private constructor() {
    this.service = getNGAR2Service();
  }

  static getInstance(): NGAR2Geocoder {
    if (!NGAR2Geocoder.instance) {
      NGAR2Geocoder.instance = new NGAR2Geocoder();
    }
    return NGAR2Geocoder.instance;
  }

  static resetInstance(): void {
    NGAR2Geocoder.instance = null;
    resetNGAR2Service();
  }

  /**
   * Geocodifica un topónimo — requiere codINE para scope municipal
   */
  async geocode(options: NgaR2GeocodingOptions): Promise<NgaR2GeocodingResult | null> {
    const results = await this.geocodeMultiple(options);
    return results.length > 0 ? results[0] : null;
  }

  /**
   * Geocodifica retornando múltiples resultados
   */
  async geocodeMultiple(options: NgaR2GeocodingOptions): Promise<NgaR2GeocodingResult[]> {
    const {
      query,
      codINE,
      type: filterType,
      maxResults = 5,
      minScore = 0.6,
    } = options;

    if (!query || query.trim().length < 2) return [];
    if (!codINE) {
      console.warn('[NGAR2Geocoder] codINE requerido para búsqueda municipal');
      return [];
    }

    // Búsqueda fuzzy scoped al municipio via NGAR2Service
    const matches = await this.service.searchInMunicipio(
      codINE, query, maxResults * 2, minScore
    );

    if (matches.length === 0) return [];

    // Filtrar por tipo si se especifica
    const filtered = filterType
      ? matches.filter(m => m.feature.type === filterType)
      : matches;

    // Construir resultados
    const results: NgaR2GeocodingResult[] = [];
    for (const { feature, score: matchScore } of filtered) {
      results.push(this.buildResult(feature, matchScore, codINE, filterType));
      if (results.length >= maxResults) break;
    }

    results.sort((a, b) => b.score - a.score);
    return results.slice(0, maxResults);
  }

  /** Construye resultado con scoring y metadata */
  private buildResult(
    feature: NgaR2Feature,
    matchScore: number,
    requestedCodINE?: string,
    requestedType?: string
  ): NgaR2GeocodingResult {
    let baseScore: number;
    let precision: number;

    if (matchScore >= 0.95) {
      baseScore = SCORE_CONFIG.exact;
      precision = PRECISION_CONFIG.exact;
    } else if (matchScore >= 0.8) {
      baseScore = SCORE_CONFIG.fuzzyHigh;
      precision = PRECISION_CONFIG.fuzzyHigh;
    } else if (matchScore >= 0.6) {
      baseScore = SCORE_CONFIG.fuzzyMedium;
      precision = PRECISION_CONFIG.fuzzyMedium;
    } else {
      baseScore = SCORE_CONFIG.fuzzyLow;
      precision = PRECISION_CONFIG.fuzzyLow;
    }

    // NGA-TYPE-01: penalizar score y aumentar precision_m según tipo geométrico real
    if (NGA_ZONE_TYPES.has(feature.type)) {
      baseScore = Math.max(0, baseScore - 15);
      precision = Math.max(precision, 80);
    } else if (NGA_LINEAR_TYPES.has(feature.type)) {
      baseScore = Math.max(0, baseScore - 5);
      precision = Math.max(precision, 30);
    }

    if (requestedCodINE && feature.codINE === requestedCodINE) {
      baseScore += SCORE_CONFIG.withMunicipioBonus;
    }
    if (requestedType && feature.type === requestedType) {
      baseScore += SCORE_CONFIG.withTypeBonus;
    }

    const confidence: 'high' | 'medium' | 'low' =
      baseScore >= 85 ? 'high' :
      baseScore >= 70 ? 'medium' : 'low';

    const parts = [feature.name];
    if (feature.type) parts.push(`(${feature.type})`);
    if (feature.municipio) parts.push(`en ${feature.municipio}`);
    const quality = matchScore >= 0.95 ? 'exacto' :
                   matchScore >= 0.8 ? 'alto' :
                   matchScore >= 0.6 ? 'medio' : 'bajo';
    parts.push(`- match ${quality}`);

    return {
      feature,
      x: feature.x,
      y: feature.y,
      score: Math.min(100, Math.round(baseScore)),
      matchScore,
      source: 'nga_r2',
      confidence,
      matchDescription: parts.join(' '),
      precision,
    };
  }

  /** Verifica si el servicio tiene datos para un municipio */
  hasMunicipio(codINE: string): boolean {
    return this.service.hasMunicipio(codINE);
  }

  /** Estadísticas */
  getStats(): { cachedMunicipios: number } {
    return { cachedMunicipios: this.service.getCacheStats().size };
  }
}

// =============================================================================
// EXPORTS
// =============================================================================

export function getNGAR2Geocoder(): NGAR2Geocoder {
  return NGAR2Geocoder.getInstance();
}

export function resetNGAR2Geocoder(): void {
  NGAR2Geocoder.resetInstance();
}

export { NGAR2Geocoder };
export default NGAR2Geocoder;

/**
 * Helper para compatibilidad con GeocodingOrchestrator
 */
export async function geocodeWithNgaR2(
  name: string,
  options: { codINE?: string } = {}
): Promise<{
  x: number;
  y: number;
  confidence: number;
  source: string;
  matchedName: string;
  municipality?: string;
  province?: string;
  /** Tipo NGA del feature (ej. 'Lugar / Paraje', 'Núcleo de Población'…).
   *  Expuesto para que PROP-B detecte zonas extensas sin llamada adicional. */
  ngaType?: string;
} | null> {
  const geocoder = getNGAR2Geocoder();

  const result = await geocoder.geocode({
    query: name,
    codINE: options.codINE,
    minScore: 0.6,
  });

  if (!result) return null;

  return {
    x: result.x,
    y: result.y,
    confidence: result.score,
    source: 'nga_r2',
    matchedName: result.feature.name,
    municipality: result.feature.municipio,
    province: result.feature.provincia,
    ngaType: result.feature.type,
  };
}
